/**
 * Helpers to map browser geolocation + Nominatim-style address payloads to our city list.
 */

function normalizeToken(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

/** Strip noise from OSM locality strings */
function simplifyLabel(s) {
  return String(s || "")
    .replace(/\b(urban|mc|municipal\s*corporation|municipality|district|metropolitan\s*region)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Known Indian city name variants → tokens we can match against slug/name */
const SYNONYM_GROUPS = [
  ["bengaluru", "bangalore"],
  ["gurugram", "gurgaon"],
  ["mumbai", "bombay"],
  ["kolkata", "calcutta"],
  ["chennai", "madras"],
  ["kochi", "cochin"],
  ["visakhapatnam", "vizag"],
  ["mysuru", "mysore"],
  ["thiruvananthapuram", "trivandrum"],
  ["bhubaneswar", "bhubaneshwar"],
];

function synonymTokens(token) {
  const t = normalizeToken(token);
  if (!t) return [];
  const out = new Set([t]);
  for (const group of SYNONYM_GROUPS) {
    const g = group.map(normalizeToken);
    if (g.some((x) => x === t)) g.forEach((x) => out.add(x));
  }
  return [...out];
}

/**
 * Collect human-readable place names from Nominatim reverse JSON (India often uses city_district, not city).
 */
export function extractCityLabelsFromNominatim(data) {
  if (!data || typeof data !== "object") return [];
  const addr = data.address || {};
  const keys = [
    "city",
    "town",
    "city_district",
    "county",
    "suburb",
    "village",
    "municipality",
    "district",
    "state_district",
  ];
  const labels = [];
  for (const key of keys) {
    const v = addr[key];
    if (typeof v === "string" && v.trim()) labels.push(simplifyLabel(v.trim()));
  }

  const dn = typeof data.display_name === "string" ? data.display_name : "";
  if (dn) {
    const parts = dn.split(",").map((s) => s.trim()).filter(Boolean);
    const indiaIdx = parts.findIndex((p) => /^india$/i.test(p));
    const end = indiaIdx >= 0 ? indiaIdx : parts.length;
    const skipState = (p) =>
      /^(india|telangana|karnataka|maharashtra|tamil\s*nadu|kerala|gujarat|west\s*bengal|delhi|haryana|punjab|rajasthan|uttar\s*pradesh|andhra\s*pradesh|bihar|odisha|assam|madhya\s*pradesh|jharkhand|chhattisgarh|uttarakhand|himachal\s*pradesh|goa|tripura|meghalaya|manipur|nagaland|arunachal\s*pradesh|mizoram|sikkim|jammu\s*(and|&)\s*kashmir|ladakh|puducherry|chandigarh)$/i.test(
        p
      );
    for (let i = 0; i < end; i++) {
      const p = parts[i];
      if (!p || /^\d{5,8}$/.test(p)) continue;
      if (skipState(p)) continue;
      if (p.length >= 3) labels.push(simplifyLabel(p));
    }
  }

  const seen = new Set();
  const out = [];
  for (const l of labels) {
    const k = l.toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(l);
  }
  return out;
}

/**
 * Match one geocode label to { name, slug } list.
 */
export function matchCityFromGeocodeLabel(label, cities) {
  if (!label || !cities?.length) return null;
  const raw = simplifyLabel(label);
  if (!raw) return null;

  const tryOne = (str) => {
    const n = normalizeToken(str);
    if (!n) return null;

    for (const token of synonymTokens(str)) {
      let best = cities.find((c) => normalizeToken(c.name) === token);
      if (best) return best;

      best = cities.find((c) => {
        const sn = normalizeToken(String(c.slug || "").replace(/-/g, ""));
        return sn === token || (token.length >= 4 && (sn.includes(token) || token.includes(sn)));
      });
      if (best) return best;

      best = cities.find((c) => {
        const name = normalizeToken(c.name);
        return (
          name.length >= 4 &&
          (name.includes(token) || token.includes(name)) &&
          Math.min(name.length, token.length) / Math.max(name.length, token.length) > 0.55
        );
      });
      if (best) return best;
    }
    return null;
  };

  return tryOne(raw);
}

/**
 * Try several labels until one matches.
 */
export function matchCityFromGeocodeLabels(labels, cities) {
  if (!labels?.length || !cities?.length) return null;
  for (const lab of labels) {
    const m = matchCityFromGeocodeLabel(lab, cities);
    if (m) return m;
  }
  return null;
}
